import _ from 'lodash'
import { utils, coreConstants } from '@adc/ui-morpheus'
import { isTrusteeType, isOnLocationSetting } from '../util/common'
import { deserializeModeSelectDropdown } from '../util/venues'

const { MODES } = coreConstants.optionLists
const {
  date: { convertToMilitaryTime, convertToNonMilitaryTime },
  formatters,
} = utils

const SCHEDULES = 'schedules'
const VENUE_ADDRESS = 'venue_address'
const VENUE_CONFIGURATION = 'venue_configuration'

const venueConfigFields = [
  'venue_classification',
  'venue_type',
  'event_types',
  'venue_setting',
  'auctioncom_auctioneer',
  'trustee_proxy_bidding',
  'web_live_status',
  'holding_venue',
  'is_minimum_bid_enabled',
  'scheduling_state_codes',
  'scheduling_seller_codes',
]
const timeFields = ['registration_start_time', 'bid_start_time', 'bid_end_time']

const OMIT_ADDRESS_VALUES_FROM_REDUX = [
  'countryCode',
  'countryPrimarySubdivision',
  'countrySecondarySubdivision',
  'municipality',
  'postalCode',
  'streetUnit',
  'coordinates',
]

/**
 * Deal with any special value translations
 */
const uiToApiValueTranslation = (value, key) => {
  let result = value

  if (key === 'event_types') {
    result = [value] // convert to single item array
  } else if (key === 'venue_url' && /^(http:\/\/|https:\/\/)$/.test(value)) {
    result = null // if just protocol, clear value
  } else if (timeFields.includes(key)) {
    result = convertToMilitaryTime(value)
  } else if (
    key === 'scheduling_state_codes' ||
    key === 'scheduling_seller_codes'
  ) {
    result = deserializeModeSelectDropdown(value)
  }
  return result
}

/**
 * Removes empty schedules and deals with schedule field conversion
 */
const processSchedules = schedules => {
  // remove any days that are null (from dropdown deselecting)
  const cleanedSchedules = schedules.map(schedule =>
    _.pickBy(schedule, val => !_.isNil(val))
  )

  // remove any schedules with nothing in them at all
  return _.filter(cleanedSchedules, s => !_.isEmpty(s)).map(schedule => {
    const result = {}
    _.each(schedule, (value, uiKey) => {
      const key = _.snakeCase(uiKey)
      result[key] = uiToApiValueTranslation(value, key)
    })
    return result
  })
}

/**
 * Clean the venue address properties that are first order props that API does not recognize
 * e.g values.postalCode values.venueAddressSuggestion
 * @params values Object values is the redux form values sent to the API
 */
const cleanAddressValues = values => {
  const cleanedValues = values || {}
  // add the venueAddressSuggestion since thats purely a FE concept
  const addressValuesToClean = [
    'venueAddressSuggestion',
    ...OMIT_ADDRESS_VALUES_FROM_REDUX,
  ]
  return _.omit(cleanedValues, addressValuesToClean)
}

const buildOnLocationAddress = values => {
  return {
    country_primary_subdivision: values.countryPrimarySubdivision,
    country_secondary_subdivision: values.countrySecondarySubdivision,
    country_code: 'US',
    street_name: '',
    street_number: '',
    municipality: '',
    postal_code: '',
    street_unit: '',
  }
}

/**
 * construct the venueAddress object, and move fields in the values object
 * @params values Object Redux form values
 */
const processAddress = values => {
  //  the api values object can be empty on reset form
  if (_.isEmpty(values)) {
    return values
  }

  /* First check if this is a on location address and needs special treatment */
  if (isOnLocationSetting(values.venueSetting)) {
    return {
      ...cleanAddressValues(values),
      venueAddress: buildOnLocationAddress(values),
    }
  }

  // Since this is in the submit phase, validation should have passed.
  // All optional required fields should be set
  // optional required fields: streetName, streetNumber, zipCode or municipality

  if (utils.address.isAnyOptReqAddressFieldEmpty(values)) {
    return cleanAddressValues(values)
  }

  const venueAddress = {
    street_name: values.venueAddressSuggestion.street_name,
    street_number: values.venueAddressSuggestion.street_number,
  }

  // add address parts
  _.each(OMIT_ADDRESS_VALUES_FROM_REDUX, value => {
    venueAddress[_.snakeCase(value)] = values[value] || null
  })

  // try to add coordinates
  if (!_.isNil(values.coordinates)) {
    venueAddress.coordinates = values.coordinates
  }

  // default the country code
  venueAddress.country_code = 'US'

  const cleanedValues = cleanAddressValues(values)
  cleanedValues.venueAddress = venueAddress
  return cleanedValues
}

/**
 * Place the key/value into the resulting object/shape
 */
const assignApiValue = (value, key, result) => {
  if (key === SCHEDULES) {
    result.schedules = processSchedules(value)
  } else if (venueConfigFields.includes(key)) {
    result.venue_configuration[key] = uiToApiValueTranslation(value, key)
  } else {
    result[key] = uiToApiValueTranslation(value, key)
  }
}

const uiToApi = values => {
  const result = { venue_configuration: {} }
  const processedValues = processAddress(values)

  _.each(processedValues, (value, uiKey) => {
    assignApiValue(value, _.snakeCase(uiKey), result)
  })

  return result
}

/**
 * API to UI functions...
 */
const apiToUiValueTranslation = (value, apiKey) => {
  let result = value

  if (apiKey === 'event_types') {
    result = value && value.length > 0 ? value[0] : value
  } else if (timeFields.includes(apiKey)) {
    result = convertToNonMilitaryTime(value)
  }
  return result
}

const convertCodes = val =>
  JSON.stringify({
    mode: (val || []).length ? MODES.INCLUDE : MODES.ALL,
    value: val || [],
  })

const assignUiValue = (value, apiKey, result) => {
  const uiKey = _.camelCase(apiKey)

  switch (apiKey) {
    case VENUE_CONFIGURATION:
      // flatten config values down
      _.each(value, (configValue, configApiKey) => {
        result[_.camelCase(configApiKey)] = apiToUiValueTranslation(
          configValue,
          configApiKey
        )
      })

      result.schedulingStateCodes = convertCodes(result.schedulingStateCodes)
      result.schedulingSellerCodes = convertCodes(result.schedulingSellerCodes)

      break
    case SCHEDULES:
      result[uiKey] = value.map(apiToUi)
      break
    case VENUE_ADDRESS: {
      const address = value
      _.each(
        _.omit(address, ['street_name', 'street_number', 'country_code']),
        (val, field) => {
          result[_.camelCase(field)] = val
        }
      )

      address.selectedAddressText = _.trim(
        `${address.street_number || ''} ${address.street_name || ''}`
      )
      result.venueAddressSuggestion = address
      break
    }
    default:
      result[uiKey] = apiToUiValueTranslation(value, apiKey)
  }

  return result
}

const apiToUi = values => {
  const result = {}

  _.each(values, (value, apiKey) => {
    assignUiValue(value, apiKey, result)
  })

  return result
}

const apiToUiDataRowsTranslation = dataRows => {
  if (!dataRows) return []
  return dataRows.map(row => {
    let newRow = _.cloneDeep(row)

    const address =
      row.venue_address && utils.address.formatAddress(row.venue_address)

    if (!_.isEmpty(address)) {
      newRow.venue_address.county_fips_code =
        address.country_secondary_subdivision
      newRow = { ...newRow, ...address }
    }

    newRow.schedules = _.map(row.schedules, apiToUi)

    if (row.venue_configuration) {
      if (row.venue_configuration.event_types) {
        const eventType = Array.isArray(row.venue_configuration.event_types)
          ? row.venue_configuration.event_types[0]
          : row.venue_configuration.event_types
        newRow.trustee = isTrusteeType(eventType) ? 'Yes' : 'No'
        newRow.venue_configuration.web_live_status = row.venue_configuration
          .web_live_status
          ? 'Active'
          : 'Inactive'
      } else {
        newRow.trustee = 'No'
      }
      newRow.venue_type = formatters.formatTitleCase(
        row.venue_configuration.venue_type
      )
    }
    return newRow
  })
}

const apiToUiDataRowTranslation = row => apiToUiDataRowsTranslation([row])[0]

export {
  apiToUi,
  uiToApi,
  apiToUiDataRowsTranslation,
  apiToUiDataRowTranslation,
}
